/**
 * QuoteSection — status="Quote" / "Quoted" workflow UI.
 *
 * Shown on OrderDetailPage above the normal sections when the order is in
 * the quote flow. Renders three modes:
 *   - "Quote"  : Admin sees the customer's request comment + a "Send quote"
 *                CTA. Customer sees a friendly "Your request is being
 *                reviewed" panel.
 *   - "Quoted" : Admin sees the current sent version + a "Revise & re-send"
 *                CTA (requires reason). Customer sees the priced summary +
 *                "Approve quote" CTA.
 *   - Anything else with a quote history: renders nothing.
 */
import { useState, useEffect } from 'react';
import { ordersApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle,
    DialogDescription, DialogFooter,
} from './ui/dialog';
import { Label } from './ui/label';
import { FileText, Send, CheckCircle2, History, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';

export const QuoteSection = ({ order, onUpdate }) => {
    const { isAdmin, user } = useAuth();
    const [sending, setSending] = useState(false);
    const [approving, setApproving] = useState(false);
    const [sendDialogOpen, setSendDialogOpen] = useState(false);
    const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
    const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
    const [rejecting, setRejecting] = useState(false);
    const [rejectReason, setRejectReason] = useState('');
    const [history, setHistory] = useState(null);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [reason, setReason] = useState('');

    const status = order?.status;
    const inQuoteFlow = status === 'Quote' || status === 'Quoted';

    // Only fetch history when the dialog opens — avoids extra load on every
    // order page render.
    useEffect(() => {
        if (!historyDialogOpen || !order?.id) return;
        let cancelled = false;
        setHistoryLoading(true);
        (async () => {
            try {
                const res = await ordersApi.getQuoteHistory(order.id);
                if (!cancelled) setHistory(res.data);
            } catch (e) {
                if (!cancelled) toast.error('Failed to load quote history');
            } finally {
                if (!cancelled) setHistoryLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [historyDialogOpen, order?.id]);

    if (!inQuoteFlow) return null;

    // Compute the active-version snapshot once per render so the summary
    // card AND the Approve-button gate share the exact same contract.
    // Approval is BLOCKED when the sent snapshot has no priced lines
    // (lines=0 OR total<=0) — this prevents the customer approving a
    // bogus zero quote and forces admin to send a corrected version.
    const versionsArr = Array.isArray(order.quote_versions) ? order.quote_versions : [];
    const currentSnapshot = versionsArr.find((v) => v.version === order.quote_current_version)
        || versionsArr[versionsArr.length - 1]
        || null;
    const _snapTotal = Number(currentSnapshot?.total);
    const _snapLines = Array.isArray(currentSnapshot?.lines) ? currentSnapshot.lines : [];
    const quoteIsApprovable = !!currentSnapshot && _snapLines.length > 0 && Number.isFinite(_snapTotal) && _snapTotal > 0;

    const versionsCount = (order.quote_versions?.length || 0) + (order.quote_current_version ? 1 : 0);
    const isResend = status === 'Quoted';

    const handleSendQuote = async () => {
        if (isResend && !reason.trim()) {
            toast.error('A revision reason is required when re-sending');
            return;
        }
        setSending(true);
        try {
            const res = await ordersApi.sendQuote(order.id, {
                message: message.trim() || undefined,
                revision_reason: reason.trim() || undefined,
            });
            toast.success(
                res.data.email_sent
                    ? `Quote V${res.data.quote_current_version} sent to customer`
                    : `Quote V${res.data.quote_current_version} saved (email not configured)`,
            );
            setSendDialogOpen(false);
            setMessage('');
            setReason('');
            if (typeof onUpdate === 'function') onUpdate();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to send quote');
        } finally {
            setSending(false);
        }
    };

    const handleApproveQuote = async () => {
        setApproving(true);
        try {
            const res = await ordersApi.approveQuote(order.id, {});
            toast.success('Quote approved — order submitted');
            if (typeof onUpdate === 'function') onUpdate();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to approve quote');
        } finally {
            setApproving(false);
        }
    };

    const handleRejectQuote = async () => {
        const trimmed = rejectReason.trim();
        if (!trimmed) {
            toast.error('Please provide a reason');
            return;
        }
        setRejecting(true);
        try {
            await ordersApi.rejectQuote(order.id, { reason: trimmed });
            toast.success('Quote declined');
            setRejectDialogOpen(false);
            setRejectReason('');
            if (typeof onUpdate === 'function') onUpdate();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to reject quote');
        } finally {
            setRejecting(false);
        }
    };

    return (
        <>
            <Card
                className={`border-2 ${status === 'Quote' ? 'border-amber-300 bg-amber-50/40' : 'border-violet-300 bg-violet-50/40'} shadow-sm`}
                data-testid="quote-section-card"
            >
                <CardHeader className="pb-2 pt-3 px-4">
                    <div className="flex items-center justify-between gap-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                            <FileText className={`h-4 w-4 ${status === 'Quote' ? 'text-amber-700' : 'text-violet-700'}`} />
                            {status === 'Quote'
                                ? 'Quote request'
                                : `Quote sent — V${order.quote_current_version || 1}`}
                        </CardTitle>
                        {versionsCount > 0 && (
                            <button
                                type="button"
                                onClick={() => setHistoryDialogOpen(true)}
                                className="text-[11px] text-slate-600 hover:text-slate-900 underline flex items-center gap-1"
                                data-testid="quote-history-btn"
                            >
                                <History className="h-3 w-3" />
                                View history
                            </button>
                        )}
                    </div>
                </CardHeader>

                <CardContent className="px-4 pb-4 pt-1 space-y-3">
                    {/* Customer's original request comment — always shown */}
                    {order.quote_request_comment && (
                        <div className="rounded-md border border-slate-200 bg-white p-3">
                            <div className="text-[10px] font-medium uppercase tracking-wider text-slate-500 mb-1 flex items-center gap-1">
                                <MessageCircle className="h-3 w-3" />
                                Customer request
                                {order.quote_requested_by_name && (
                                    <span className="text-slate-400 normal-case font-normal ml-1">
                                        · {order.quote_requested_by_name}
                                    </span>
                                )}
                            </div>
                            <p className="text-sm text-slate-800 whitespace-pre-wrap" data-testid="quote-request-comment">
                                {order.quote_request_comment}
                            </p>
                        </div>
                    )}

                    {/* Internal admin instructions (from linked SKU) — admin only */}
                    {isAdmin && order.quote_admin_instructions && (
                        <div className="rounded-md border border-blue-200 bg-blue-50 p-3">
                            <div className="text-[10px] font-medium uppercase tracking-wider text-blue-800 mb-1">
                                Internal note (from SKU config)
                            </div>
                            <p className="text-xs text-blue-900 whitespace-pre-wrap">
                                {order.quote_admin_instructions}
                            </p>
                        </div>
                    )}

                    {/* Quote summary — immutable snapshot of what the customer is
                        about to approve. Rendered for BOTH admin and customer so
                        each side sees the exact same locked numbers. Falls back
                        to a correction banner when the snapshot is empty / zero
                        so Approve can never be clicked against a bogus quote. */}
                    {status === 'Quoted' && (() => {
                        const versions = Array.isArray(order.quote_versions) ? order.quote_versions : [];
                        const currentVersion = order.quote_current_version;
                        const snapshot = versions.find((v) => v.version === currentVersion)
                            || versions[versions.length - 1]
                            || null;
                        const lines = Array.isArray(snapshot?.lines) ? snapshot.lines : [];
                        const totalRaw = snapshot?.total;
                        const total = typeof totalRaw === 'number' ? totalRaw : parseFloat(totalRaw);
                        const currency = snapshot?.currency || 'EUR';
                        const hasValidPrice = !!snapshot && lines.length > 0 && Number.isFinite(total) && total > 0;
                        const fmt = (n) => {
                            if (!Number.isFinite(n)) return '—';
                            const parts = n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                            if (currency === 'EUR') return `€${parts}`;
                            if (currency === 'DKK' || currency === 'SEK' || currency === 'NOK') return `${parts} ${currency === 'DKK' ? 'kr' : currency}`;
                            if (currency === 'GBP') return `£${parts}`;
                            if (currency === 'USD') return `$${parts}`;
                            return `${parts} ${currency}`;
                        };
                        return (
                            <div
                                className="rounded-md border border-emerald-200 bg-white p-3"
                                data-testid="quote-sent-summary"
                            >
                                <div className="flex items-center justify-between mb-2">
                                    <div className="text-[10px] font-medium uppercase tracking-wider text-emerald-800">
                                        Quoted price — V{currentVersion || 1}
                                    </div>
                                    {snapshot?.sent_at && (
                                        <div className="text-[10px] text-slate-500">
                                            Sent {new Date(snapshot.sent_at).toLocaleDateString()}
                                            {snapshot?.sent_by_name ? ` · ${snapshot.sent_by_name}` : ''}
                                        </div>
                                    )}
                                </div>

                                {!hasValidPrice ? (
                                    <div
                                        className="rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900"
                                        data-testid="quote-summary-correction-required"
                                    >
                                        <strong>Quote is incomplete</strong> — the sent quote has no priced lines yet.
                                        Approval is blocked until our team sends an updated version.
                                    </div>
                                ) : (
                                    <>
                                        <table className="w-full text-xs">
                                            <thead>
                                                <tr className="text-slate-500 border-b border-slate-200">
                                                    <th className="text-left py-1 font-medium">Description</th>
                                                    <th className="text-right py-1 font-medium">Qty</th>
                                                    <th className="text-right py-1 font-medium">Unit price</th>
                                                    <th className="text-right py-1 font-medium">Line total</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {lines.map((ln, idx) => {
                                                    const q = Number(ln?.quantity) || 0;
                                                    const u = Number(ln?.unit_price);
                                                    const lt = Number(ln?.total);
                                                    return (
                                                        <tr key={ln?.id || `l${idx}`} className="border-b border-slate-100 last:border-0">
                                                            <td className="py-1.5 text-slate-800 whitespace-pre-wrap pr-2">
                                                                {ln?.description || ln?.sales_sku || `Line ${idx + 1}`}
                                                                {ln?.notes && (
                                                                    <div className="text-[10px] text-slate-500 italic mt-0.5">{ln.notes}</div>
                                                                )}
                                                            </td>
                                                            <td className="py-1.5 text-right text-slate-700 tabular-nums">
                                                                {q || '—'}
                                                            </td>
                                                            <td className="py-1.5 text-right text-slate-700 tabular-nums">
                                                                {Number.isFinite(u) ? fmt(u) : '—'}
                                                            </td>
                                                            <td className="py-1.5 text-right text-slate-900 font-medium tabular-nums">
                                                                {Number.isFinite(lt) ? fmt(lt) : '—'}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                            <tfoot>
                                                <tr className="border-t-2 border-emerald-300">
                                                    <td colSpan={3} className="py-2 text-right text-sm font-medium text-slate-700">
                                                        Grand total
                                                    </td>
                                                    <td
                                                        className="py-2 text-right text-base font-bold text-emerald-900 tabular-nums"
                                                        data-testid="quote-summary-grand-total"
                                                    >
                                                        {fmt(total)}
                                                    </td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                        {snapshot?.cover_message && (
                                            <div className="mt-2 rounded border border-slate-200 bg-slate-50 p-2 text-xs text-slate-700 whitespace-pre-wrap">
                                                <div className="text-[10px] font-medium uppercase tracking-wider text-slate-500 mb-1">
                                                    Message from our team
                                                </div>
                                                {snapshot.cover_message}
                                            </div>
                                        )}
                                        <p className="text-[10px] text-slate-500 italic mt-2">
                                            Prices shown are {currency}, exclusive of VAT where applicable. Delivery
                                            terms will be confirmed on the final invoice. By approving, you lock in
                                            exactly these amounts for order <strong>#{String(order.order_number || '').padStart(6, '0')}</strong>.
                                        </p>
                                    </>
                                )}
                            </div>
                        );
                    })()}

                    {/* Action row */}
                    {isAdmin ? (
                        <div className="flex flex-wrap items-center gap-2 pt-1">
                            <p className="text-xs text-slate-600 flex-1 min-w-[180px]">
                                {status === 'Quote'
                                    ? 'Prepare the customer quotation using the pricing in Invoice Basis above, then send it to the customer for approval.'
                                    : 'Customer has been notified. Revise the invoice lines below and click "Revise & re-send" to send an updated version.'}
                            </p>
                            <Button
                                size="sm"
                                onClick={() => setSendDialogOpen(true)}
                                className={status === 'Quote' ? 'bg-amber-600 hover:bg-amber-700' : 'bg-violet-600 hover:bg-violet-700'}
                                data-testid="open-send-quote-dialog"
                            >
                                <Send className="h-3 w-3 mr-1" />
                                {status === 'Quote' ? 'Send quote to customer' : 'Revise & re-send'}
                            </Button>
                        </div>
                    ) : (
                        <div className="flex flex-wrap items-center gap-2 pt-1">
                            {status === 'Quote' ? (
                                <p className="text-xs text-slate-700">
                                    Your quote request has been received. Our team is preparing your quote and
                                    will send it to you shortly.
                                </p>
                            ) : (
                                <>
                                    <p className="text-xs text-slate-700 flex-1 min-w-[200px]">
                                        {quoteIsApprovable
                                            ? 'Your quote is ready for review. Once approved, we will start production.'
                                            : 'Approval is blocked — the current quote version has no priced lines yet. Our team will send a corrected version shortly.'}
                                    </p>
                                    <Button
                                        size="sm"
                                        onClick={handleApproveQuote}
                                        disabled={approving || rejecting || !quoteIsApprovable}
                                        className="bg-emerald-600 hover:bg-emerald-700"
                                        data-testid="approve-quote-btn"
                                        title={quoteIsApprovable ? '' : 'This quote has no priced lines — awaiting admin correction'}
                                    >
                                        <CheckCircle2 className="h-3 w-3 mr-1" />
                                        {approving ? 'Approving…' : 'Approve quote'}
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => setRejectDialogOpen(true)}
                                        disabled={approving || rejecting}
                                        className="border-slate-300 text-slate-700 hover:bg-slate-100"
                                        data-testid="reject-quote-btn"
                                    >
                                        Decline
                                    </Button>
                                </>
                            )}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Send / Re-send quote dialog */}
            <Dialog open={sendDialogOpen} onOpenChange={setSendDialogOpen}>
                <DialogContent className="sm:max-w-lg" data-testid="send-quote-dialog">
                    <DialogHeader>
                        <DialogTitle>
                            {isResend
                                ? `Revise & re-send quote — V${(order.quote_current_version || 1) + 1}`
                                : 'Send quote to customer'}
                        </DialogTitle>
                        <DialogDescription>
                            {isResend
                                ? 'The customer will receive an updated quote. The previous version stays in the audit history.'
                                : 'Make sure the invoice lines below capture the full quote. The customer will receive an email with a link to review and approve.'}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-3 py-2">
                        {isResend && (
                            <div>
                                <Label htmlFor="quote-revision-reason" className="text-xs">
                                    Revision reason <span className="text-red-600">*</span>
                                </Label>
                                <textarea
                                    id="quote-revision-reason"
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    rows={2}
                                    placeholder="e.g. Corrected colour to translucent green after client feedback"
                                    className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                                    data-testid="quote-revision-reason"
                                    required
                                />
                            </div>
                        )}
                        <div>
                            <Label htmlFor="quote-cover-message" className="text-xs">
                                Cover message to customer (optional)
                            </Label>
                            <textarea
                                id="quote-cover-message"
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                rows={3}
                                placeholder="Included in the email above the quote link."
                                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                                data-testid="quote-cover-message"
                            />
                        </div>
                        <p className="text-[11px] text-slate-500">
                            The full priced breakdown is taken from the current Invoice Basis / Sales
                            Document lines below.
                        </p>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" size="sm" onClick={() => setSendDialogOpen(false)} disabled={sending}>
                            Cancel
                        </Button>
                        <Button
                            size="sm"
                            onClick={handleSendQuote}
                            disabled={sending || (isResend && !reason.trim())}
                            data-testid="confirm-send-quote-btn"
                        >
                            <Send className="h-3 w-3 mr-1" />
                            {sending
                                ? 'Sending…'
                                : isResend
                                    ? `Send V${(order.quote_current_version || 1) + 1}`
                                    : 'Send quote'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Customer reject-quote dialog */}
            <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
                <DialogContent className="sm:max-w-md" data-testid="reject-quote-dialog">
                    <DialogHeader>
                        <DialogTitle>Decline quote</DialogTitle>
                        <DialogDescription>
                            Tell us briefly why — this helps our team adjust and come back with
                            a better offer. Declining will cancel this quote request; nothing will
                            be produced.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-2">
                        <Label htmlFor="quote-reject-reason" className="text-xs">
                            Reason <span className="text-red-600">*</span>
                        </Label>
                        <textarea
                            id="quote-reject-reason"
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                            rows={3}
                            placeholder="e.g. Price higher than expected / timing changed / going with another option"
                            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                            data-testid="quote-reject-reason"
                            maxLength={2000}
                        />
                    </div>
                    <DialogFooter>
                        <Button size="sm" variant="outline" onClick={() => setRejectDialogOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            size="sm"
                            onClick={handleRejectQuote}
                            disabled={rejecting || !rejectReason.trim()}
                            className="bg-slate-700 hover:bg-slate-800"
                            data-testid="confirm-reject-quote-btn"
                        >
                            {rejecting ? 'Declining…' : 'Decline quote'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Quote history dialog */}
            <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
                <DialogContent className="sm:max-w-3xl max-h-[80vh] flex flex-col" data-testid="quote-history-dialog">
                    <DialogHeader>
                        <DialogTitle>Quote history</DialogTitle>
                        <DialogDescription>
                            Every version of this quote, from the initial customer request through to
                            approval.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="flex-1 overflow-y-auto space-y-2 -mx-6 px-6">
                        {historyLoading && (
                            <div className="text-xs text-slate-500 py-4 text-center">Loading history…</div>
                        )}
                        {!historyLoading && history && (
                            <>
                                {/* Original request row */}
                                <div className="border rounded-md p-3 bg-amber-50 border-amber-200 text-xs">
                                    <div className="flex items-center justify-between mb-1">
                                        <span className="px-1.5 py-0.5 rounded bg-amber-600 text-white font-semibold">Request</span>
                                        <span className="text-slate-500 text-[11px]">
                                            {history.quote_requested_at ? new Date(history.quote_requested_at).toLocaleString('da-DK') : '—'}
                                            {history.quote_requested_by_name && (
                                                <span className="ml-2">by <span className="font-medium text-slate-700">{history.quote_requested_by_name}</span></span>
                                            )}
                                        </span>
                                    </div>
                                    {history.quote_request_comment && (
                                        <p className="whitespace-pre-wrap text-slate-800">{history.quote_request_comment}</p>
                                    )}
                                </div>

                                {/* Sent versions */}
                                {(history.versions || []).map((v) => (
                                    <div
                                        key={v.version}
                                        className={`border rounded-md p-3 text-xs ${v.version === history.quote_current_version ? 'bg-violet-50 border-violet-200' : 'bg-slate-50 border-slate-200'}`}
                                        data-testid={`quote-version-row-v${v.version}`}
                                    >
                                        <div className="flex items-center justify-between mb-1">
                                            <div className="flex items-center gap-2">
                                                <span className={`px-1.5 py-0.5 rounded text-white font-semibold ${v.version === history.quote_current_version ? 'bg-violet-600' : 'bg-slate-400'}`}>
                                                    V{v.version}
                                                </span>
                                                <span className="text-slate-600">
                                                    {v.version === history.quote_current_version ? 'Active' : 'Superseded'}
                                                </span>
                                                <span className="text-slate-700 font-semibold tabular-nums">
                                                    {Number(v.total || 0).toLocaleString('da-DK', { minimumFractionDigits: 2 })} {v.currency}
                                                </span>
                                            </div>
                                            <span className="text-slate-500 text-[11px]">
                                                {v.sent_at ? new Date(v.sent_at).toLocaleString('da-DK') : ''}
                                                {v.sent_by_name && (
                                                    <span className="ml-2">by <span className="font-medium text-slate-700">{v.sent_by_name}</span></span>
                                                )}
                                            </span>
                                        </div>
                                        {v.revision_reason && (
                                            <div className="mt-1 text-slate-600">
                                                <span className="text-[10px] uppercase text-slate-400">Reason: </span>
                                                {v.revision_reason}
                                            </div>
                                        )}
                                        {v.cover_message && (
                                            <div className="mt-1 text-slate-600 italic">"{v.cover_message}"</div>
                                        )}
                                        {Array.isArray(v.lines) && v.lines.length > 0 && (
                                            <table className="w-full mt-2 text-[11px] border-t border-slate-200 pt-2">
                                                <tbody>
                                                    {v.lines.map((line, i) => (
                                                        <tr key={i}>
                                                            <td className="py-0.5 pr-2 text-slate-700">{line.description}</td>
                                                            <td className="py-0.5 text-slate-500 text-right tabular-nums">
                                                                {line.quantity} × {Number(line.unit_price).toLocaleString('da-DK', { minimumFractionDigits: 2 })}
                                                            </td>
                                                            <td className="py-0.5 pl-2 text-slate-800 text-right tabular-nums font-medium">
                                                                {Number(line.total).toLocaleString('da-DK', { minimumFractionDigits: 2 })} {line.currency}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        )}
                                    </div>
                                ))}

                                {/* Approval */}
                                {history.quote_approved_at && (
                                    <div className="border rounded-md p-3 bg-emerald-50 border-emerald-200 text-xs">
                                        <div className="flex items-center justify-between">
                                            <span className="px-1.5 py-0.5 rounded bg-emerald-600 text-white font-semibold flex items-center gap-1">
                                                <CheckCircle2 className="h-3 w-3" /> Approved
                                            </span>
                                            <span className="text-slate-500 text-[11px]">
                                                {new Date(history.quote_approved_at).toLocaleString('da-DK')}
                                                {history.quote_approved_by_name && (
                                                    <span className="ml-2">by <span className="font-medium text-slate-700">{history.quote_approved_by_name}</span></span>
                                                )}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" size="sm" onClick={() => setHistoryDialogOpen(false)}>
                            Close
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default QuoteSection;
